import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StudentManagement = () => {
  const navigate = useNavigate();

  // === Core Data States ===
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState(new Set());

  // === Admin Info ===
  const [currentAdmin, setCurrentAdmin] = useState(null);

  // === UI States ===
  const [activeTab, setActiveTab] = useState('students');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [gpsSettings, setGpsSettings] = useState({
    enabled: true,
    studentGpsEnabled: true,
    studentAutoCheckIn: true,
    teacherAutoCheckIn: true,
    staffAutoCheckIn: true,
    latitude: '',
    longitude: '',
    rangeMeters: 15,
    accuracyMeters: 20
  });
  const [gpsMessage, setGpsMessage] = useState('');

  // === Video Management States ===
  const [videos, setVideos] = useState([]);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [videoFormData, setVideoFormData] = useState({
    title: '',
    url: '',
    description: '',
    platform: 'youtube'
  });
  const [videoFormErrors, setVideoFormErrors] = useState({});

  // === Games Management States ===
  const [games, setGames] = useState([]);
  const [showAddGameModal, setShowAddGameModal] = useState(false);
  const [showEditGameModal, setShowEditGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [gameFormData, setGameFormData] = useState({
    title: '',
    description: '',
    componentName: '',
    difficulty: 'Medium',
    category: 'Logic',
    suitableClasses: []
  });
  const [gameFormErrors, setGameFormErrors] = useState({});

  // === Screen Time Management States ===
  const [screenTimeLimits, setScreenTimeLimits] = useState([]);
  const [screenTimeUsage, setScreenTimeUsage] = useState([]);
  const [showSetLimitsModal, setShowSetLimitsModal] = useState(false);
  const [showClassLimitsModal, setShowClassLimitsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [limitsFormData, setLimitsFormData] = useState({
    study: 120,
    assignment: 90,
    kidstoon: 60,
    games: 45,
    other: 30
  });
  const [classLimitsFormData, setClassLimitsFormData] = useState({
    study: 120,
    assignment: 90,
    kidstoon: 60,
    games: 45,
    other: 30
  });
  const [usageStats, setUsageStats] = useState({});

  // === Statistics ===
  const [stats, setStats] = useState({
    totalStudents: 0,
    studentsByClass: {},
    averageAge: 0
  });

  // === Form States ===
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    className: '',
    age: '',
    parentContact: '',
    address: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // ========================= INITIALIZATION =========================
  useEffect(() => {
    loadAllData();
    loadCurrentAdmin();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [students]);

  const loadCurrentAdmin = () => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    setCurrentAdmin(adminUser);
  };

  // ========================= DATA FETCHING =========================
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [studentsRes, classesRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.STUDENTS}`),
        fetch(`${API_ENDPOINTS.CLASSES}`)
      ]);

      const studentsData = await studentsRes.json();
      const classesData = await classesRes.json();

      setStudents(studentsData);
      setClasses(classesData);
      loadGpsSettings();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGpsSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS);
      if (response.ok) {
        const data = await response.json();
        setGpsSettings((current) => ({ ...current, ...data }));
      }
    } catch (error) {
      console.error('Error loading GPS settings:', error);
    }
  };

  const saveGpsSettings = async () => {
    setGpsMessage('');
    try {
      const payload = {
        ...gpsSettings,
        latitude: Number(gpsSettings.latitude),
        longitude: Number(gpsSettings.longitude),
        rangeMeters: Number(gpsSettings.rangeMeters),
        accuracyMeters: Number(gpsSettings.accuracyMeters)
      };
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to save GPS settings');
      setGpsSettings((current) => ({ ...current, ...result.settings }));
      setGpsMessage('Student GPS attendance settings saved.');
    } catch (error) {
      setGpsMessage(error.message || 'Failed to save GPS settings.');
    }
  };

  const loadVideos = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.VIDEOS}`);
      const videosData = await response.json();
      setVideos(videosData);
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };

  const loadGames = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/games/all`);
      const gamesData = await response.json();
      setGames(gamesData);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const calculateStats = () => {
    const studentsByClass = {};
    let totalAge = 0;
    let validAges = 0;

    students.forEach(student => {
      // Count by class
      if (!studentsByClass[student.className]) {
        studentsByClass[student.className] = 0;
      }
      studentsByClass[student.className]++;

      // Calculate average age
      if (student.age && !isNaN(student.age)) {
        totalAge += parseInt(student.age);
        validAges++;
      }
    });

    setStats({
      totalStudents: students.length,
      studentsByClass,
      averageAge: validAges > 0 ? Math.round((totalAge / validAges) * 10) / 10 : 0
    });
  };

  // ========================= FORM HANDLING =========================
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const openAddModal = () => {
    setFormData({
      firstName: '',
      lastName: '',
      className: '',
      age: '',
      parentContact: '',
      address: '',
      username: '',
      password: '',
      confirmPassword: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (student) => {
    setFormData({
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      className: student.className || '',
      age: student.age || '',
      parentContact: student.parentContact || '',
      address: student.address || '',
      username: student.username || '',
      password: '',
      confirmPassword: ''
    });
    setEditingStudent(student);
    setShowEditModal(true);
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    const required = ['firstName', 'lastName', 'className', 'parentContact', 'username'];
    if (!editingStudent) required.push('password'); // Password required for new students

    required.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });

    // Age validation
    if (formData.age && (isNaN(formData.age) || formData.age < 3 || formData.age > 25)) {
      errors.age = 'Age must be between 3 and 25';
    }

    // Username validation
    if (formData.username && (formData.username.length < 3 || !/^[a-zA-Z0-9_]+$/.test(formData.username))) {
      errors.username = 'Username must be at least 3 characters and contain only letters, numbers, and underscores';
    }

    // Password validation (only for new students or when changing password)
    if (!editingStudent && formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    // Confirm password validation
    if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      // Remove confirmPassword before sending
      const { confirmPassword, ...studentData } = formData;

      if (editingStudent) {
        let response;

        if (selectedFile) {
          const formDataUpload = new FormData();
          formDataUpload.append('profilePicture', selectedFile);
          formDataUpload.append('firstName', studentData.firstName || '');
          formDataUpload.append('lastName', studentData.lastName || '');
          formDataUpload.append('className', studentData.className || '');
          formDataUpload.append('age', studentData.age || '');
          formDataUpload.append('parentContact', studentData.parentContact || '');
          formDataUpload.append('address', studentData.address || '');

          response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/${editingStudent.username}/profile-picture`, {
            method: 'PUT',
            body: formDataUpload
          });
        } else {
          response = await fetch(`${API_ENDPOINTS.STUDENTS}/${editingStudent.username}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              firstName: studentData.firstName || '',
              lastName: studentData.lastName || '',
              className: studentData.className || '',
              age: studentData.age || '',
              parentContact: studentData.parentContact || '',
              address: studentData.address || ''
            })
          });
        }

        const result = await response.json().catch(() => ({}));

        if (response.ok) {
          setShowAddModal(false);
          setShowEditModal(false);
          setEditingStudent(null);
          setSelectedFile(null);
          setProfilePicturePreview(null);
          loadAllData();
          alert('Student updated successfully!');
        } else {
          alert(result.error || result.details || 'Failed to update student');
        }
      } else {
        // Add new student - use approval system for management admin
        if (currentAdmin?.role === 'management_admin') {
          // Create approval request instead of direct creation
          const approvalData = {
            action: 'create',
            resourceType: 'student',
            resourceId: `student-${Date.now()}`,
            resourceName: `${studentData.firstName} ${studentData.lastName}`,
            reason: `Student registration request for ${studentData.firstName} ${studentData.lastName} (${studentData.className})`,
            studentData: studentData
          };

          const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'admin-email': adminUser.email
            },
            body: JSON.stringify(approvalData)
          });

          const result = await response.json().catch(() => ({}));

          if (response.ok) {
            setShowAddModal(false);
            setShowEditModal(false);
            setEditingStudent(null);
            alert('Student registration request submitted for approval. You will be notified once it is reviewed.');
          } else {
            alert(result.error || result.details || 'Failed to submit approval request');
          }
        } else {
          // Super admin can add directly
          const response = await fetch(`${API_ENDPOINTS.STUDENTS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(studentData)
          });

          const result = await response.json().catch(() => ({}));

          if (response.ok) {
            setShowAddModal(false);
            setShowEditModal(false);
            setEditingStudent(null);
            loadAllData();
            alert('Student added successfully!');
          } else {
            alert(result.error || result.details || 'Failed to add student');
          }
        }
      }
    } catch (error) {
      console.error('Error saving student:', error);
      alert(`Failed to save student. ${error.message || 'Please try again.'}`);
    }
  };

  const handleResetPassword = async (student) => {
    const confirmMessage = `Are you sure you want to reset the password for "${student.firstName} ${student.lastName}" (${student.username})Loading\n\n` +
      `The password will be reset to: 123456\n` +
      `The student will be forced to change their password on next login.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: student.username })
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Password reset successful!\n\nStudent: ${student.firstName} ${student.lastName}\nDefault Password: ${result.defaultPassword}\n\nThe student will be required to change their password on next login.`);
        loadAllData(); // Refresh to show updated student data
      } else {
        alert(result.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password. Please try again.');
    }
  };

  const handleDelete = async (student) => {
    if (currentAdmin?.role === 'management_admin') {
      // Management admin must use approval system for deletions
      const confirmMessage = `Are you sure you want to request deletion of "${student.firstName} ${student.lastName}" (${student.username})Loading\n\n` +
        `This action requires Super Admin approval before the student will be removed from the system.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        const approvalData = {
          action: 'delete',
          resourceType: 'student',
          resourceId: student.username,
          resourceName: `${student.firstName} ${student.lastName}`,
          reason: `Student deletion request for ${student.firstName} ${student.lastName} (${student.username})`
        };

        const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'admin-email': adminUser.email
          },
          body: JSON.stringify(approvalData)
        });

        if (response.ok) {
          alert('Student deletion request submitted for approval. The student will remain in the system until approved by Super Admin.');
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to submit deletion request');
        }
      } catch (error) {
        console.error('Error submitting deletion request:', error);
        alert('Failed to submit deletion request. Please try again.');
      }
    } else {
      // Super admin can delete directly
      if (!window.confirm(`Are you sure you want to delete "${student.firstName} ${student.lastName}" (${student.username})?`)) {
        return;
      }

      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/${student.username}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          loadAllData();
          alert('Student deleted successfully!');
        } else {
          alert('Failed to delete student');
        }
      } catch (error) {
        console.error('Error deleting student:', error);
        alert('Failed to delete student. Please try again.');
      }
    }
  };

  // ========================= SELECTION HANDLING =========================
  const handleSelectStudent = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.username)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudents.size === 0) return;

    if (currentAdmin?.role === 'management_admin') {
      // Management admin must use approval system for bulk deletions
      if (!window.confirm(`Are you sure you want to request deletion of ${selectedStudents.size} student(s)Loading\n\nThis action requires Super Admin approval before the students will be removed from the system.`)) {
        return;
      }

      try {
        const bulkApprovalPromises = Array.from(selectedStudents).map(async (username) => {
          const student = students.find(s => s.username === username);
          if (!student) return;

          const approvalData = {
            action: 'delete',
            resourceType: 'student',
            resourceId: username,
            resourceName: `${student.firstName} ${student.lastName}`,
            reason: `Bulk student deletion request for ${student.firstName} ${student.lastName} (${username})`
          };

          const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
          return fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'admin-email': adminUser.email
            },
            body: JSON.stringify(approvalData)
          });
        });

        await Promise.all(bulkApprovalPromises);
        setSelectedStudents(new Set());
        alert(`${selectedStudents.size} student deletion request(s) submitted for approval. The students will remain in the system until approved by Super Admin.`);
      } catch (error) {
        console.error('Error submitting bulk deletion requests:', error);
        alert('Failed to submit some deletion requests. Please try again.');
      }
    } else {
      // Super admin can delete directly
      if (!window.confirm(`Are you sure you want to delete ${selectedStudents.size} student(s)?`)) {
        return;
      }

      try {
        const deletePromises = Array.from(selectedStudents).map(username =>
          fetch(`${API_ENDPOINTS.BASE_URL}/api/students/${username}`, { method: 'DELETE' })
        );

        await Promise.all(deletePromises);
        setSelectedStudents(new Set());
        loadAllData();
        alert(`${selectedStudents.size} student(s) deleted successfully!`);
      } catch (error) {
        console.error('Error bulk deleting students:', error);
        alert('Failed to delete some students. Please try again.');
      }
    }
  };

  // ========================= VIDEO MANAGEMENT =========================
  const handleVideoFormChange = (e) => {
    const { name, value } = e.target;
    setVideoFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field errors when user starts typing
    if (videoFormErrors[name]) {
      setVideoFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateVideoForm = () => {
    const errors = {};

    if (!videoFormData.title || !videoFormData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!videoFormData.url || !videoFormData.url.trim()) {
      errors.url = 'Video URL is required';
    } else {
      // Basic URL validation
      try {
        new URL(videoFormData.url);
      } catch (error) {
        errors.url = 'Invalid URL format';
      }
    }

    return errors;
  };

  const handleAddVideo = async () => {
    const errors = validateVideoForm();
    setVideoFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.VIDEOS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(videoFormData)
      });

      if (response.ok) {
        setShowAddVideoModal(false);
        setVideoFormData({
          title: '',
          url: '',
          description: '',
          platform: 'youtube'
        });
        loadVideos();
        alert('Video added successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add video');
      }
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Failed to add video. Please try again.');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/videos/${videoId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadVideos();
        alert('Video deleted successfully!');
      } else {
        alert('Failed to delete video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video. Please try again.');
    }
  };

  // ========================= GAMES MANAGEMENT =========================
  const handleGameFormChange = (e) => {
    const { name, value } = e.target;
    setGameFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field errors when user starts typing
    if (gameFormErrors[name]) {
      setGameFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateGameForm = () => {
    const errors = {};

    if (!gameFormData.title || !gameFormData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!gameFormData.description || !gameFormData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (!gameFormData.componentName || !gameFormData.componentName.trim()) {
      errors.componentName = 'Component name is required';
    }

    return errors;
  };

  const handleGameClassToggle = (className) => {
    setGameFormData(prev => ({
      ...prev,
      suitableClasses: prev.suitableClasses.includes(className)
        ? prev.suitableClasses.filter(c => c !== className)
        : [...prev.suitableClasses, className]
    }));
  };

  const openEditGameModal = (game) => {
    setGameFormData({
      title: game.title,
      description: game.description,
      componentName: game.componentName,
      difficulty: game.difficulty,
      category: game.category,
      suitableClasses: game.suitableClasses || []
    });
    setEditingGame(game);
    setShowEditGameModal(true);
  };

  const handleAddGame = async () => {
    const errors = validateGameForm();
    setGameFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gameFormData)
      });

      if (response.ok) {
        setShowAddGameModal(false);
        setGameFormData({
          title: '',
          description: '',
          componentName: '',
          difficulty: 'Medium',
          category: 'Logic',
          suitableClasses: []
        });
        loadGames();
        alert('Game added successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add game');
      }
    } catch (error) {
      console.error('Error adding game:', error);
      alert('Failed to add game. Please try again.');
    }
  };

  const handleEditGame = async () => {
    const errors = validateGameForm();
    setGameFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/games/${editingGame.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gameFormData)
      });

      if (response.ok) {
        setShowEditGameModal(false);
        setEditingGame(null);
        setGameFormData({
          title: '',
          description: '',
          componentName: '',
          difficulty: 'Medium',
          category: 'Logic',
          suitableClasses: []
        });
        loadGames();
        alert('Game updated successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update game');
      }
    } catch (error) {
      console.error('Error updating game:', error);
      alert('Failed to update game. Please try again.');
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/games/${gameId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadGames();
        alert('Game deleted successfully!');
      } else {
        alert('Failed to delete game');
      }
    } catch (error) {
      console.error('Error deleting game:', error);
      alert('Failed to delete game. Please try again.');
    }
  };

  // ========================= SCREEN TIME MANAGEMENT =========================
  const loadScreenTimeData = async () => {
    try {
      const [limitsRes, usageRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/limits`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/usage`)
      ]);

      const limitsData = await limitsRes.json();
      const usageData = await usageRes.json();

      setScreenTimeLimits(limitsData);
      setScreenTimeUsage(usageData);
    } catch (error) {
      console.error('Error loading screen time data:', error);
    }
  };

  const handleLimitsFormChange = (e) => {
    const { name, value } = e.target;
    setLimitsFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClassLimitsFormChange = (e) => {
    const { name, value } = e.target;
    setClassLimitsFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openSetLimitsModal = (student) => {
    setSelectedStudent(student);
    // Load existing limits for this student
    fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/limits/${student.username}`)
      .then(res => res.json())
      .then(data => {
        setLimitsFormData(data.limits || {
          study: 120,
          assignment: 90,
          kidstoon: 60,
          games: 45,
          other: 30
        });
        setShowSetLimitsModal(true);
      })
      .catch(error => {
        console.error('Error loading student limits:', error);
        setLimitsFormData({
          study: 120,
          assignment: 90,
          kidstoon: 60,
          games: 45,
          other: 30
        });
        setShowSetLimitsModal(true);
      });
  };

  const handleSetStudentLimits = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: selectedStudent.username,
          limits: limitsFormData
        })
      });

      if (response.ok) {
        setShowSetLimitsModal(false);
        setSelectedStudent(null);
        loadScreenTimeData();
        alert('Screen time limits updated successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update limits');
      }
    } catch (error) {
      console.error('Error updating limits:', error);
      alert('Failed to update screen time limits. Please try again.');
    }
  };

  const handleSetClassLimits = async () => {
    if (!selectedClass || selectedClass === 'all') {
      alert('Please select a specific class');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/limits/class/${selectedClass}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limits: classLimitsFormData })
      });

      if (response.ok) {
        const result = await response.json();
        setShowClassLimitsModal(false);
        loadScreenTimeData();
        alert(`Screen time limits updated for ${result.updatedCount} students in ${selectedClass}`);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update class limits');
      }
    } catch (error) {
      console.error('Error updating class limits:', error);
      alert('Failed to update class screen time limits. Please try again.');
    }
  };

  const handleResetUsage = async (username) => {
    if (!window.confirm(`Are you sure you want to reset all screen time usage for this student?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/screen-time/reset/${username}`, {
        method: 'POST'
      });

      if (response.ok) {
        loadScreenTimeData();
        alert('Screen time usage reset successfully!');
      } else {
        alert('Failed to reset usage');
      }
    } catch (error) {
      console.error('Error resetting usage:', error);
      alert('Failed to reset screen time usage. Please try again.');
    }
  };

  const getTodayUsage = (username) => {
    const today = new Date().toISOString().split('T')[0];
    return screenTimeUsage.filter(item =>
      item.username === username &&
      item.date === today
    ).reduce((total, item) => total + item.duration, 0);
  };

  const getStudentLimits = (username) => {
    const studentLimits = screenTimeLimits.find(limit => limit.username === username);
    return studentLimits ? studentLimits.limits : {
      study: 120,
      assignment: 90,
      kidstoon: 60,
      games: 45,
      other: 30
    };
  };

  // ========================= FILTERING & SEARCH =========================
  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.parentContact.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesClass = selectedClass === 'all' || student.className === selectedClass;

    return matchesSearch && matchesClass;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading student management...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
              <p className="text-sm text-gray-600">Manage and organize student records</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('students')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'students'
                    ? 'app-vibrant-tab-active'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Students Students Management
              </button>
              <button
                onClick={() => {
                  setActiveTab('videos');
                  loadVideos(); // Load videos when switching to videos tab
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'videos'
                    ? 'app-vibrant-tab-active'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Video Online Video Management
              </button>
              <button
                onClick={() => {
                  setActiveTab('games');
                  loadGames(); // Load games when switching to games tab
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'games'
                    ? 'app-vibrant-tab-active'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Games Kids Games Management
              </button>
              <button
                onClick={() => {
                  setActiveTab('screen-time');
                  loadScreenTimeData(); // Load screen time data when switching to screen time tab
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'screen-time'
                    ? 'app-vibrant-tab-active'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Screen Time Screen Time Management
              </button>
              <button
                onClick={() => {
                  setActiveTab('gps-attendance');
                  loadGpsSettings();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'gps-attendance'
                    ? 'app-vibrant-tab-active'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                GPS Attendance
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'students' && (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Students</div>
                <h3 className="text-lg font-semibold text-gray-800">{stats.totalStudents}</h3>
                <p className="text-sm text-gray-600">Total Students</p>
              </div>

              <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">School</div>
                <h3 className="text-lg font-semibold text-blue-800">{Object.keys(stats.studentsByClass).length}</h3>
                <p className="text-sm text-blue-600">Active Classes</p>
              </div>

              <div className="bg-green-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Stats</div>
                <h3 className="text-lg font-semibold text-green-800">{stats.averageAge}</h3>
                <p className="text-sm text-green-600">Average Age</p>
              </div>

              <div className="bg-purple-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Growth</div>
                <h3 className="text-lg font-semibold text-purple-800">
                  {Object.keys(stats.studentsByClass).length > 0 ?
                    Math.max(...Object.values(stats.studentsByClass)) : 0}
                </h3>
                <p className="text-sm text-purple-600">Largest Class</p>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Students</label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name, username, or contact..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Class</label>
                    <select
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Classes</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={openAddModal}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
                  >
                    <span>+</span>
                    <span>Add Student</span>
                  </button>

                  {selectedStudents.size > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition duration-200 flex items-center space-x-2"
                    >
                      <span>Delete</span>
                      <span>Delete ({selectedStudents.size})</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Students Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudents.length === 0 ? (
                <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">Students</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {students.length === 0 ? 'No Students Yet' : 'No Students Found'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {students.length === 0
                      ? 'Start by adding your first student to the system.'
                      : 'Try adjusting your search or filter criteria.'}
                  </p>
                  {students.length === 0 && (
                    <button
                      onClick={openAddModal}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                    >
                      Add First Student
                    </button>
                  )}
                </div>
              ) : (
                filteredStudents.map((student) => (
                  <div key={student.username} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                    <div className="p-6">
                      {/* Student Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm">
                            {student.profilePicture ? (
                              <img
                                src={student.profilePicture}
                                alt={`${student.firstName} ${student.lastName}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Hide the broken image
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : null}
                            {/* Always show initials as fallback */}
                            <div className={`w-full h-full bg-blue-100 flex items-center justify-center ${student.profilePicture ? 'hidden' : ''}`}>
                              <span className="text-lg font-bold text-blue-600">
                                {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {student.firstName} {student.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">@{student.username}</p>
                          </div>
                        </div>

                        <input
                          type="checkbox"
                          checked={selectedStudents.has(student.username)}
                          onChange={() => handleSelectStudent(student.username)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </div>

                      {/* Student Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Class:</span>
                          <span className="text-sm font-medium text-gray-900">{student.className}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Age:</span>
                          <span className="text-sm font-medium text-gray-900">{student.age || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Contact:</span>
                          <span className="text-sm font-medium text-gray-900 truncate max-w-32" title={student.parentContact}>
                            {student.parentContact}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(student)}
                          className="flex-1 bg-blue-600 text-white py-2 px-2 rounded-md hover:bg-blue-700 transition duration-200 text-xs font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleResetPassword(student)}
                          className="flex-1 bg-yellow-600 text-white py-2 px-2 rounded-md hover:bg-yellow-700 transition duration-200 text-xs font-medium"
                          title="Reset password to '123456' and force student to change it"
                        >
                          Reset PW
                        </button>
                        <button
                          onClick={() => handleDelete(student)}
                          className="flex-1 bg-red-600 text-white py-2 px-2 rounded-md hover:bg-red-700 transition duration-200 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Results Summary */}
            {filteredStudents.length > 0 && (
              <div className="mt-6 text-center text-gray-600">
                Showing {filteredStudents.length} of {students.length} students
              </div>
            )}
          </>
        )}

        {activeTab === 'gps-attendance' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Student GPS Attendance Settings</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow students to mark attendance either with the generated security PIN or with GPS when their device is inside the school radius.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={saveGpsSettings}
                  className="bg-emerald-700 text-white px-6 py-2 rounded-md hover:bg-emerald-800"
                >
                  Save GPS Settings
                </button>
              </div>

              {gpsMessage && (
                <div className={`mb-5 rounded-lg p-3 text-sm ${gpsMessage.includes('saved') ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>
                  {gpsMessage}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {[
                  ['latitude', 'School Latitude'],
                  ['longitude', 'School Longitude'],
                  ['rangeMeters', 'Allowed Radius (meters)'],
                  ['accuracyMeters', 'Required GPS Accuracy (meters)']
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <input
                      type="number"
                      step={key.includes('Meters') ? '1' : '0.000001'}
                      value={gpsSettings[key]}
                      onChange={(e) => setGpsSettings({ ...gpsSettings, [key]: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ['enabled', 'Enable GPS validation for attendance'],
                  ['studentGpsEnabled', 'Allow students to mark attendance by GPS'],
                  ['studentAutoCheckIn', 'Student GPS check-in can count automatically'],
                  ['teacherAutoCheckIn', 'Keep teacher GPS auto check-in enabled'],
                  ['staffAutoCheckIn', 'Keep staff GPS auto check-in enabled']
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
                    <span className="text-sm font-semibold text-gray-800">{label}</span>
                    <input
                      type="checkbox"
                      checked={Boolean(gpsSettings[key])}
                      onChange={(e) => setGpsSettings({ ...gpsSettings, [key]: e.target.checked })}
                      className="h-5 w-5 text-emerald-700 rounded focus:ring-emerald-500"
                    />
                  </label>
                ))}
              </div>

              <div className="mt-6 bg-slate-50 rounded-lg border border-slate-200 p-4 text-sm text-slate-700">
                Students still keep the existing generated drop-off/pickup PIN flow. GPS adds a second attendance method that validates distance and accuracy before the record is saved.
              </div>
            </div>
          </div>
        )}

        {activeTab === 'videos' && (
          <>
            {/* Video Management Header */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Online Video Management</h2>
                  <p className="text-sm text-gray-600">Add safe video links for students to watch</p>
                </div>
                <button
                  onClick={() => setShowAddVideoModal(true)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Add Video</span>
                </button>
              </div>
            </div>

            {/* Videos Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.length === 0 ? (
                <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">Video</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Videos Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Start by adding your first safe video link for students to watch.
                  </p>
                  <button
                    onClick={() => setShowAddVideoModal(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                  >
                    Add First Video
                  </button>
                </div>
              ) : (
                videos.map((video) => (
                  <div key={video.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                    <div className="p-6">
                      {/* Video Header */}
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{video.title}</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span>Video {video.platform}</span>
                          <span>?</span>
                          <span>Added {new Date(video.addedDate).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Video Description */}
                      {video.description && (
                        <p className="text-sm text-gray-700 mb-4 line-clamp-2">{video.description}</p>
                      )}

                      {/* Video URL */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 truncate" title={video.url}>
                          {video.url}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => window.open(video.url, '_blank')}
                          className="flex-1 bg-green-600 text-white py-2 px-2 rounded-md hover:bg-green-700 transition duration-200 text-xs font-medium"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video.id)}
                          className="flex-1 bg-red-600 text-white py-2 px-2 rounded-md hover:bg-red-700 transition duration-200 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Videos Summary */}
            {videos.length > 0 && (
              <div className="mt-6 text-center text-gray-600">
                Total Videos: {videos.length}
              </div>
            )}
          </>
        )}

        {activeTab === 'games' && (
          <>
            {/* Games Management Header */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Kids Games Management</h2>
                  <p className="text-sm text-gray-600">Add and manage educational games for students</p>
                </div>
                <button
                  onClick={() => setShowAddGameModal(true)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Add Game</span>
                </button>
              </div>
            </div>

            {/* Games Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {games.length === 0 ? (
                <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">Games</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Games Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Start by adding your first educational game for students to play.
                  </p>
                  <button
                    onClick={() => setShowAddGameModal(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                  >
                    Add First Game
                  </button>
                </div>
              ) : (
                games.map((game) => (
                  <div key={game.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                    <div className="p-6">
                      {/* Game Header */}
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{game.title}</h3>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${game.difficulty === 'Easy' ? 'bg-green-100 text-green-800' : game.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {game.difficulty}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {game.category}
                          </span>
                        </div>
                      </div>

                      {/* Game Description */}
                      <p className="text-sm text-gray-700 mb-4">{game.description}</p>

                      {/* Game Classes */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">Suitable for:</p>
                        <div className="flex flex-wrap gap-1">
                          {game.suitableClasses.map(cls => (
                            <span key={cls} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {cls}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Game Component */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500">Component: {game.componentName}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditGameModal(game)}
                          className="flex-1 bg-blue-600 text-white py-2 px-2 rounded-md hover:bg-blue-700 transition duration-200 text-xs font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => alert(`Game: ${game.title}\nComponent: ${game.componentName}\nThis game is ready for students to play!`)}
                          className="flex-1 bg-green-600 text-white py-2 px-2 rounded-md hover:bg-green-700 transition duration-200 text-xs font-medium"
                        >
                          Test Game
                        </button>
                        <button
                          onClick={() => handleDeleteGame(game.id)}
                          className="flex-1 bg-red-600 text-white py-2 px-2 rounded-md hover:bg-red-700 transition duration-200 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Games Summary */}
            {games.length > 0 && (
              <div className="mt-6 text-center text-gray-600">
                Total Games: {games.length}
              </div>
            )}
          </>
        )}

        {activeTab === 'screen-time' && (
          <>
            {/* Screen Time Management Header */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Screen Time Management</h2>
                  <p className="text-sm text-gray-600">Set and monitor screen time limits for different activities</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowClassLimitsModal(true)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
                  >
                    <span>Books</span>
                    <span>Set Class Limits</span>
                  </button>
                  <button
                    onClick={() => alert('Screen time usage statistics will be shown here')}
                    className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition duration-200 flex items-center space-x-2"
                  >
                    <span>Stats</span>
                    <span>View Statistics</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Screen Time Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Time</div>
                <h3 className="text-lg font-semibold text-gray-800">{screenTimeLimits.length}</h3>
                <p className="text-sm text-gray-600">Students with Limits</p>
              </div>

              <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Study</div>
                <h3 className="text-lg font-semibold text-blue-800">
                  {screenTimeLimits.reduce((sum, limit) => sum + (limit.limits?.study || 0), 0)} min
                </h3>
                <p className="text-sm text-blue-600">Avg Study Limit</p>
              </div>

              <div className="bg-green-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Games</div>
                <h3 className="text-lg font-semibold text-green-800">
                  {screenTimeLimits.reduce((sum, limit) => sum + (limit.limits?.games || 0), 0)} min
                </h3>
                <p className="text-sm text-green-600">Avg Games Limit</p>
              </div>

              <div className="bg-purple-50 rounded-lg shadow p-6 text-center">
                <div className="text-3xl mb-2">Other</div>
                <h3 className="text-lg font-semibold text-purple-800">
                  {screenTimeUsage.filter(item => item.date === new Date().toISOString().split('T')[0]).length}
                </h3>
                <p className="text-sm text-purple-600">Sessions Today</p>
              </div>
            </div>

            {/* Students Screen Time Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {students.length === 0 ? (
                <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">Time</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Students Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Add students first to manage their screen time limits.
                  </p>
                </div>
              ) : (
                filteredStudents.map((student) => {
                  const limits = getStudentLimits(student.username);
                  const todayUsage = getTodayUsage(student.username);
                  const totalLimit = Object.values(limits).reduce((sum, limit) => sum + limit, 0);

                  return (
                    <div key={student.username} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                      <div className="p-6">
                        {/* Student Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm">
                              {student.profilePicture ? (
                                <img
                                  src={student.profilePicture}
                                  alt={`${student.firstName} ${student.lastName}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full bg-blue-100 flex items-center justify-center ${student.profilePicture ? 'hidden' : ''}`}>
                                <span className="text-sm font-bold text-blue-600">
                                  {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900">
                                {student.firstName} {student.lastName}
                              </h3>
                              <p className="text-xs text-gray-600">@{student.username}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            todayUsage < totalLimit * 0.8 ? 'bg-green-100 text-green-800' :
                            todayUsage < totalLimit ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {todayUsage}/{totalLimit}min
                          </span>
                        </div>

                        {/* Screen Time Limits */}
                        <div className="space-y-2 mb-4">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Study Study:</span>
                              <span className="font-medium">{limits.study}min</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Note Assignment:</span>
                              <span className="font-medium">{limits.assignment}min</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">📺 Kidstoon:</span>
                              <span className="font-medium">{limits.kidstoon}min</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Games Games:</span>
                              <span className="font-medium">{limits.games}min</span>
                            </div>
                          </div>
                        </div>

                        {/* Today's Usage */}
                        <div className="mb-4">
                          <h4 className="text-xs font-medium text-gray-700 mb-2">Today's Usage</h4>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                todayUsage / totalLimit > 0.9 ? 'bg-red-500' :
                                todayUsage / totalLimit > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min((todayUsage / totalLimit) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Used: {todayUsage}min of {totalLimit}min daily limit
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openSetLimitsModal(student)}
                            className="flex-1 bg-blue-600 text-white py-2 px-2 rounded-md hover:bg-blue-700 transition duration-200 text-xs font-medium"
                          >
                            Set Limits
                          </button>
                          <button
                            onClick={() => handleResetUsage(student.username)}
                            className="flex-1 bg-yellow-600 text-white py-2 px-2 rounded-md hover:bg-yellow-700 transition duration-200 text-xs font-medium"
                          >
                            Reset Usage
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Screen Time Summary */}
            {students.length > 0 && (
              <div className="mt-6 text-center text-gray-600">
                Showing screen time management for {filteredStudents.length} of {students.length} students
              </div>
            )}
          </>
        )}
      </div>

      {/* Set Student Limits Modal */}
      {showSetLimitsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Set Screen Time Limits - {selectedStudent?.firstName} {selectedStudent?.lastName}
              </h3>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Study (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="study"
                    value={limitsFormData.study}
                    onChange={handleLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Assignment (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="assignment"
                    value={limitsFormData.assignment}
                    onChange={handleLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📺 Kidstoon (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="kidstoon"
                    value={limitsFormData.kidstoon}
                    onChange={handleLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Games Games (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="games"
                    value={limitsFormData.games}
                    onChange={handleLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Other Other Activities (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="other"
                    value={limitsFormData.other}
                    onChange={handleLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleSetStudentLimits}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Update Limits
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSetLimitsModal(false);
                      setSelectedStudent(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Set Class Limits Modal */}
      {showClassLimitsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Set Screen Time Limits for Class
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Class
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Select a class...</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Study (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="study"
                    value={classLimitsFormData.study}
                    onChange={handleClassLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Assignment (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="assignment"
                    value={classLimitsFormData.assignment}
                    onChange={handleClassLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📺 Kidstoon (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="kidstoon"
                    value={classLimitsFormData.kidstoon}
                    onChange={handleClassLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Games Games (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="games"
                    value={classLimitsFormData.games}
                    onChange={handleClassLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Other Other Activities (minutes per day)
                  </label>
                  <input
                    type="number"
                    name="other"
                    value={classLimitsFormData.other}
                    onChange={handleClassLimitsFormChange}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleSetClassLimits}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Update Class Limits
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowClassLimitsModal(false);
                      setSelectedClass('all');
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {showAddModal ? 'Add New Student' : 'Edit Student'}
              </h3>

              <form className="space-y-6">
                {/* Personal Information */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Profile Personal Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter first name"
                      />
                      {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter last name"
                      />
                      {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age
                      </label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleFormChange}
                        min="3"
                        max="25"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.age ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter age"
                      />
                      {formErrors.age && <p className="text-red-500 text-xs mt-1">{formErrors.age}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Class <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="className"
                        value={formData.className}
                        onChange={handleFormChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.className ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Class</option>
                        {classes.map(cls => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                      {formErrors.className && <p className="text-red-500 text-xs mt-1">{formErrors.className}</p>}
                    </div>
                  </div>
                </div>

                {/* Profile Picture */}
                {showEditModal && (
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Photo Profile Picture</h4>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-200 shadow-lg">
                          <img
                            src={profilePicturePreview || editingStudent?.profilePicture || '/fallback-data/default-avatar.png'}
                            alt={`${formData.firstName} ${formData.lastName}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/fallback-data/default-avatar.png';
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Upload New Profile Picture
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setSelectedFile(file);
                                // Create preview URL
                                const reader = new FileReader();
                                reader.onload = (e) => setProfilePicturePreview(e.target.result);
                                reader.readAsDataURL(file);
                              } else {
                                setSelectedFile(null);
                                setProfilePicturePreview(null);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Supported formats: JPG, PNG, GIF, WebP (Max 2MB)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Contact Contact Information</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Parent/Guardian Contact <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="parentContact"
                        value={formData.parentContact}
                        onChange={handleFormChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.parentContact ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Email or phone number"
                      />
                      {formErrors.parentContact && <p className="text-red-500 text-xs mt-1">{formErrors.parentContact}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Home Address
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleFormChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter complete home address"
                      />
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Security Account Information</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleFormChange}
                        disabled={showEditModal} // Can't change username when editing
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          formErrors.username ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Choose a username"
                      />
                      {formErrors.username && <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>}
                      {showEditModal && (
                        <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                      )}
                    </div>

                    {!showEditModal && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleFormChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              formErrors.password ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Create a password"
                          />
                          {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm Password <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleFormChange}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Confirm your password"
                          />
                          {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>}
                        </div>
                      </>
                    )}

                    {showEditModal && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <p className="text-sm text-yellow-800">
                          Warning Password changes are not available in edit mode. Students can change their passwords from their profile page.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    {showAddModal ? 'Add Student' : 'Update Student'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingStudent(null);
                      setSelectedFile(null);
                      setProfilePicturePreview(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Video Modal */}
      {showAddVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Add Video Link</h3>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={videoFormData.title}
                    onChange={handleVideoFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      videoFormErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter video title"
                  />
                  {videoFormErrors.title && <p className="text-red-500 text-xs mt-1">{videoFormErrors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={videoFormData.url}
                    onChange={handleVideoFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      videoFormErrors.url ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                  {videoFormErrors.url && <p className="text-red-500 text-xs mt-1">{videoFormErrors.url}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform
                  </label>
                  <select
                    name="platform"
                    value={videoFormData.platform}
                    onChange={handleVideoFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="youtube">YouTube</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    value={videoFormData.description}
                    onChange={handleVideoFormChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief description of the video content"
                  />
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleAddVideo}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Add Video
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddVideoModal(false);
                      setVideoFormData({
                        title: '',
                        url: '',
                        description: '',
                        platform: 'youtube'
                      });
                      setVideoFormErrors({});
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Game Modal */}
      {showAddGameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Add Educational Game</h3>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={gameFormData.title}
                    onChange={handleGameFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter game title"
                  />
                  {gameFormErrors.title && <p className="text-red-500 text-xs mt-1">{gameFormErrors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={gameFormData.description}
                    onChange={handleGameFormChange}
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the game and its educational value"
                  />
                  {gameFormErrors.description && <p className="text-red-500 text-xs mt-1">{gameFormErrors.description}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="componentName"
                    value={gameFormData.componentName}
                    onChange={handleGameFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.componentName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., ColorMatchingGame"
                  />
                  {gameFormErrors.componentName && <p className="text-red-500 text-xs mt-1">{gameFormErrors.componentName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level
                  </label>
                  <select
                    name="difficulty"
                    value={gameFormData.difficulty}
                    onChange={handleGameFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={gameFormData.category}
                    onChange={handleGameFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Logic">Logic</option>
                    <option value="Math">Math</option>
                    <option value="Language">Language</option>
                    <option value="Cognitive">Cognitive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suitable Classes <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Select the classes that can access this game</p>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {classes.map(cls => (
                      <label key={cls} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={gameFormData.suitableClasses.includes(cls)}
                          onChange={() => handleGameClassToggle(cls)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span>{cls}</span>
                      </label>
                    ))}
                  </div>
                  {gameFormData.suitableClasses.length === 0 && (
                    <p className="text-red-500 text-xs mt-1">At least one class must be selected</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleAddGame}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Add Game
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddGameModal(false);
                      setGameFormData({
                        title: '',
                        description: '',
                        componentName: '',
                        difficulty: 'Medium',
                        category: 'Logic',
                        suitableClasses: []
                      });
                      setGameFormErrors({});
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Game Modal */}
      {showEditGameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Edit Educational Game</h3>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={gameFormData.title}
                    onChange={handleGameFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter game title"
                  />
                  {gameFormErrors.title && <p className="text-red-500 text-xs mt-1">{gameFormErrors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={gameFormData.description}
                    onChange={handleGameFormChange}
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the game and its educational value"
                  />
                  {gameFormErrors.description && <p className="text-red-500 text-xs mt-1">{gameFormErrors.description}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="componentName"
                    value={gameFormData.componentName}
                    onChange={handleGameFormChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      gameFormErrors.componentName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., ColorMatchingGame"
                  />
                  {gameFormErrors.componentName && <p className="text-red-500 text-xs mt-1">{gameFormErrors.componentName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level
                  </label>
                  <select
                    name="difficulty"
                    value={gameFormData.difficulty}
                    onChange={handleGameFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={gameFormData.category}
                    onChange={handleGameFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Logic">Logic</option>
                    <option value="Math">Math</option>
                    <option value="Language">Language</option>
                    <option value="Cognitive">Cognitive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suitable Classes <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Select the classes that can access this game</p>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {classes.map(cls => (
                      <label key={cls} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={gameFormData.suitableClasses.includes(cls)}
                          onChange={() => handleGameClassToggle(cls)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span>{cls}</span>
                      </label>
                    ))}
                  </div>
                  {gameFormData.suitableClasses.length === 0 && (
                    <p className="text-red-500 text-xs mt-1">At least one class must be selected</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleEditGame}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Update Game
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditGameModal(false);
                      setEditingGame(null);
                      setGameFormData({
                        title: '',
                        description: '',
                        componentName: '',
                        difficulty: 'Medium',
                        category: 'Logic',
                        suitableClasses: []
                      });
                      setGameFormErrors({});
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
